import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { ProductsState } from 'src/app/shared/state/ProductsState';
import { CustomerReferential, Referential } from '../models/CustomerReferential';
import { CustomerDetailsRequest } from '../models/request.model';
import { CustomerSetupResponse } from '../models/response.model';
import { CamSpecName } from '../models/response.model';

export function getCustomerFormGroup(formBuilder: FormBuilder): FormGroup {
  return formBuilder.group({
    customerInfo: formBuilder.group({
      code: ['', Validators.required],
      customerName: ['', Validators.required],
      customerType: ['', Validators.required],
      associatedTo: [''],
      active: [false],
      btmFlag: [false],
      rfidCustFlag: [false],
      address1: [''],
      address2: [''],
      city: [''],
      stateProvince: [''],
      postalCode: [''],
      countryCode: [''],
      contactName: [''],
      email: ['', Validators.email],
      phoneNumber: [''],
      faxNumber: [''],
      salesId: [''],
      routeId: [''],
      enterpriseId: [''],
      shipToCode: ['']
    }),
    customerCamSpec: formBuilder.group({
      camSpecName: ['', Validators.required],
      repairLimitsCheckApplyWhen: ['']
    }),
    customerSettings: formBuilder.group({
      turnAroundTime: [0, Validators.required],
      casingCreditCode: [''],
      casingPurchaseCode: [''],
      michelinBillTo: [''],
      michelinShipTo: [''],
      preferredTreadCode: [''],
      casingGradeCode: [''],
      freeRepairCode: [''],
      remainingTreadDepth: ['', Validators.required],
      brand: ['', Validators.required],
      rememberBrand: ['', Validators.required],
      rememberCasingNote: ['', Validators.required],
      rtDot: ['', Validators.required],
      note: ['', sqlInjectionValidator()],
      wheelProcessing: [false],
      customerRejects: ['', Validators.required],
      stockRejects: ['', Validators.required],
      scrapDisposalCharge: ['', Validators.required],
      scrapCasingPlant: [false, Validators.required],
      centralReject: [false, Validators.required],
      includeScrapCasingRejectCustomer: [false, Validators.required],
      finishedLabelFormat: [''],
      rejectReturnLabelFormat: [''],
      rejectScrapLabelFormat: [''],
      barcodeLabelFormat: [''],
      interfaceID1: [''],
      interfaceID2: ['']
    })
  });
}

export function populateToggleButtons(customerFormGroup: FormGroup, customer: CustomerSetupResponse) {
  customerFormGroup.get('customerInfo').patchValue({
    active: customer.customerInfo.active === 'Y',
    btmFlag: customer.customerInfo.btmFlag === 'Y',
    rfidCustFlag: customer.customerInfo.rfidCustFlag === 'Y'
  });
  customerFormGroup.get('customerSettings').patchValue({
    wheelProcessing: customer.customerSettings.wheelProcessing === 'Y',
    scrapCasingPlant: customer.customerSettings.scrapCasingPlant === 'Y',
    centralReject: customer.customerSettings.centralReject === 'Y',
    includeScrapCasingRejectCustomer: customer.customerSettings.includeScrapCasingRejectCustomer === 'Y'
  });
}

export function populateDropdowns(customerFormGroup, dropdownOptions, customer: CustomerSetupResponse) {
  const matchedcasingCredit = dropdownOptions['CasingCredit'].find((option) => option.code === customer.customerSettings.casingCreditCode);
  const matchedcasingPurchase = dropdownOptions['CasingPurchase'].find(
    (option) => option.code === customer.customerSettings.casingPurchaseCode
  );
  const matchedpreferredTread = dropdownOptions['PreferredTreadSpec'].find(
    (option) => option.code === customer.customerSettings.preferredTreadCode
  );
  const matchedcasingGrade = dropdownOptions['CasingGradeTable'].find(
    (option) => option.code === customer.customerSettings.casingGradeCode
  );
  const matchedfreeRepairCode = dropdownOptions['FreeRepairTable'].find(
    (option) => option.code === customer.customerSettings.freeRepairCode
  );
  const matchedscrapDisposalCharge = dropdownOptions['DisposalCharge'].find(
    (option) => option.code === customer.customerSettings.scrapDisposalCharge
  );
  const matchedfinishedLabelFormat = dropdownOptions['PrintingTemplate'].find(
    (option) => option.code === customer.customerSettings.finishedLabelFormat && option.description === 'FINISHED_GOODS'
  );
  const matchedrejectReturnLabelFormat = dropdownOptions['PrintingTemplate'].find(
    (option) => option.code === customer.customerSettings.rejectReturnLabelFormat && option.description === 'REJECT_RETURN'
  );
  const matchedrejectScrapLabelFormat = dropdownOptions['PrintingTemplate'].find(
    (option) => option.code === customer.customerSettings.rejectScrapLabelFormat && option.description === 'REJECT_SCRAP'
  );
  const matchedbarcodeLabelFormat = dropdownOptions['PrintingTemplate'].find(
    (option) => option.code === customer.customerSettings.barcodeLabelFormat && option.description === 'BAR_CODE'
  );
  customerFormGroup.get('customerSettings').patchValue({
    casingCreditCode: matchedcasingCredit ? matchedcasingCredit.id : null,
    casingPurchaseCode: matchedcasingPurchase ? matchedcasingPurchase.id : null,
    preferredTreadCode: matchedpreferredTread ? matchedpreferredTread.id : null,
    casingGradeCode: matchedcasingGrade ? matchedcasingGrade.id : null,
    freeRepairCode: matchedfreeRepairCode ? matchedfreeRepairCode.id : null,
    scrapDisposalCharge: matchedscrapDisposalCharge ? matchedscrapDisposalCharge.id : null,
    finishedLabelFormat: matchedfinishedLabelFormat ? matchedfinishedLabelFormat.id : null,
    rejectReturnLabelFormat: matchedrejectReturnLabelFormat ? matchedrejectReturnLabelFormat.id : null,
    rejectScrapLabelFormat: matchedrejectScrapLabelFormat ? matchedrejectScrapLabelFormat.id : null,
    barcodeLabelFormat: matchedbarcodeLabelFormat ? matchedbarcodeLabelFormat.id : null
  });
}

export function getAllCustomerDropdownKeys() {
  return [
    'CasingCredit',
    'CasingPurchase',
    'PreferredTreadSpec',
    'CasingGradeTable',
    'FreeRepairTable',
    'DisposalCharge',
    'PrintingTemplate'
  ];
}

export function getDropdownOptions(key: string, store: Store): Referential[] {
  const allDropdownOptions: CustomerReferential = store.selectSnapshot(ProductsState.customerReferential);
  return allDropdownOptions[key];
}

export function mapToCustomerDetailsRequest(
  customer: CustomerSetupResponse,
  jsonFormValue: any,
  camSpecOptions: CamSpecName[],
  labelTemplateOptions: Referential[]
): CustomerDetailsRequest {
  const customerSettings = jsonFormValue.customerSettings;
  const customerInfo = jsonFormValue.customerInfo;
  const camSpec = findCustomerCamSpecByName(camSpecOptions, jsonFormValue.customerCamSpec.camSpecName);

  return {
    customerCamSpec: {
      camSpecId: camSpec.id,
      camSpecName: camSpec.name,
      repairLimitsCheckApplyWhen: jsonFormValue.customerCamSpec.repairLimitsCheckApplyWhen
    },
    customerSettings: {
      casingCreditCodeId: customerSettings.casingCreditCode,
      casingPurchaseCodeId: customerSettings.casingPurchaseCode,
      michelinBillTo: customerSettings.michelinBillTo,
      michelinShipTo: customerSettings.michelinShipTo,
      preferredTreadCodeId: customerSettings.preferredTreadCode,
      casingGradeCodeId: customerSettings.casingGradeCode,
      freeRepairCodeId: customerSettings.freeRepairCode,
      turnAroundTime: customerSettings.turnAroundTime,
      remainingTreadDepth: customerSettings.remainingTreadDepth,
      brand: customerSettings.brand,
      rememberBrand: customerSettings.rememberBrand,
      rememberCasingNote: customerSettings.rememberCasingNote,
      rtDot: customerSettings.rtDot,
      note: customerSettings.note,
      wheelProcessing: customerSettings.wheelProcessing ? 'Y' : 'N',
      customerRejects: customerSettings.customerRejects,
      stockRejects: customerSettings.stockRejects,
      disposalChargesId: customerSettings.scrapDisposalCharge,
      scrapCasingPlant: customerSettings.scrapCasingPlant ? 'Y' : 'N',
      centralReject: customerSettings.centralReject ? 'Y' : 'N',
      includeScrapCasingRejectCustomer: customerSettings.includeScrapCasingRejectCustomer ? 'Y' : 'N',
      finishedLabelId: customerSettings.finishedLabelFormat,
      rejectReturnLabelId: customerSettings.rejectReturnLabelFormat,
      rejectScrapLabelId: customerSettings.rejectScrapLabelFormat,
      barcodeLabelId: customerSettings.barcodeLabelFormat,
      interfaceID1: customerSettings.interfaceID1,
      interfaceID2: customerSettings.interfaceID2,
      customerImportEnabled: customer.customerSettings.customerImportEnabled,
      finishedLabelFormat: findLabelTemplateById(labelTemplateOptions, customerSettings.finishedLabelFormat),
      rejectReturnLabelFormat: findLabelTemplateById(labelTemplateOptions, customerSettings.rejectReturnLabelFormat),
      rejectScrapLabelFormat: findLabelTemplateById(labelTemplateOptions, customerSettings.rejectScrapLabelFormat),
      barcodeLabelFormat: findLabelTemplateById(labelTemplateOptions, customerSettings.barcodeLabelFormat)
    },
    customerInfo: {
      code: customerInfo.code,
      customerName: customerInfo.customerName,
      active: customerInfo.active ? 'Y' : 'N',
      customerType: customerInfo.customerType,
      btmFlag: customerInfo.btmFlag ? 'Y' : 'N',
      rfidCustFlag: customerInfo.rfidCustFlag ? 'Y' : 'N',
      address1: customerInfo.address1,
      address2: customerInfo.address2,
      city: customerInfo.city,
      stateProvince: customerInfo.stateProvince,
      postalCode: customerInfo.postalCode,
      country: customerInfo.countryCode,
      countryCode: customerInfo.countryCode,
      contactName: customerInfo.contactName,
      email: customerInfo.email,
      phoneNumber: customerInfo.phoneNumber,
      faxNumber: customerInfo.faxNumber,
      salesId: customerInfo.salesId,
      routeId: customerInfo.routeId,
      enterpriseId: customerInfo.enterpriseId,
      shipToCode: customerInfo.shipToCode,
      dealerId: customer.customerInfo.dealerId,
      optCustCode: customer.customerInfo.optCustCode,
      reuseBarCodeNum: customer.customerInfo.reuseBarCodeNum,
      printBarcodeEnable: customer.customerInfo.printBarcodeEnable,
      associatedTo: customer.customerInfo.associatedTo
    }
  };
}

export function findCustomerCamSpecByName(camSpecs: CamSpecName[], camSpecName: string): CamSpecName | undefined {
  return camSpecs.find((camSpec) => camSpec.name === camSpecName);
}

export function findLabelTemplateById(labelOptions: Referential[], labelId: string): string | undefined {
  if (!labelId) {
    return null;
  }

  const labelTemplate = labelOptions.find((option) => option.id === labelId);
  return labelTemplate ? labelTemplate.code : null;
}

export function sqlInjectionValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const sqlInjectionPattern = /(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|TRUNCATE|EXEC|UNION|--|;|=)/i;
    if (value && sqlInjectionPattern.test(value)) {
      return { sqlInjection: true };
    }
    return null;
  };
}
